/**
 * useHoldings - auto-detected external holdings (Crypto page + Accounts card)
 *
 * Loads ALL transactions once, runs the shared capital-move detector over
 * them (services/holdings.ts + watchlist groups), and layers on local
 * adjustments. Refreshes on transaction data events and on adjustment
 * changes (window event - keeps the Accounts card and /crypto page in sync
 * within the same tab).
 */

import { useState, useEffect, useCallback } from 'react';
import { transactionService } from '@services/transactions';
import { dataEvents } from '@services/dataEvents';
import { loadWatchGroups } from '@services/watchlist';
import {
  HoldingsSnapshot,
  computeHoldings,
  loadAdjustments,
  HOLDINGS_CHANGED_EVENT,
} from '@services/holdings';

interface UseHoldingsResult {
  holdings: HoldingsSnapshot[];
  loading: boolean;
  refresh: () => void;
}

export function useHoldings(): UseHoldingsResult {
  const [holdings, setHoldings] = useState<HoldingsSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const groups = loadWatchGroups();

        // Skip the (all-time) transaction fetch entirely when nothing is
        // tracked - most users without capital-move groups pay zero cost.
        const hasTracked = groups.some(
          (g) => g.enabled && g.excludeFromSpending && g.terms.length > 0
        );
        if (!hasTracked) {
          if (!cancelled) {
            setHoldings([]);
            setLoading(false);
          }
          return;
        }

        const adjustments = await loadAdjustments();
        const transactions = await transactionService.getFiltered({
          limit: 100000,
          offset: 0,
        });
        if (cancelled) return;

        setHoldings(computeHoldings(transactions, groups, adjustments));
        setLoading(false);
      } catch (err) {
        console.error('[Holdings] Failed to compute holdings:', err);
        if (!cancelled) {
          setHoldings([]);
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Recompute when transactions change or adjustments are edited anywhere
  useEffect(() => {
    const unsubTxns = dataEvents.on('transactions', refresh);
    const onAdjustments = () => refresh();
    window.addEventListener(HOLDINGS_CHANGED_EVENT, onAdjustments);
    return () => {
      unsubTxns();
      window.removeEventListener(HOLDINGS_CHANGED_EVENT, onAdjustments);
    };
  }, [refresh]);

  return { holdings, loading, refresh };
}
